// SkipUrsStageDialog.tsx
import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    TextField,
    Stack,
    Box,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { LoadingButton } from "@mui/lab";
import { Localized } from "@fluent/react";
import { ProcessStageOverview, skipUrsStage } from "../validationProcessApi";
import { useAppDispatch } from "../../../../app/hooks";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { CancelButton } from "../../../../app/components/CancelButton";

type Props = {
    open: boolean;
    processId: string;
    onClose: () => void;
    onSuccess: (stage: ProcessStageOverview) => void;
};

export const SkipUrsStageDialog: React.FC<Props> = ({
    open,
    processId,
    onClose,
    onSuccess,
}) => {
    const dispatch = useAppDispatch();

    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);

    const handleClose = () => {
        if (saving)
            return;
        setServerError(null);
        setComment("");
        onClose();
    };

    const handleConfirm = async () => {
        setSaving(true);
        setServerError(null);

        try {
            const result = await dispatch(skipUrsStage({ processId, comment })).unwrap();

            dispatch(showSuccess("saved"));
            onSuccess(result);
        } catch (e) {
            setServerError("error");
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle
                sx={{
                    bgcolor: (theme) => theme.palette.grey[200],
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 0.75,
                }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <WarningAmberIcon color="error" />
                    <Box>
                        <Localized id="stage-skip-urs-title">
                            Pomiń etap URS
                        </Localized>
                    </Box>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                <DialogContentText sx={{ mb: 2 }}>
                    <Localized id="stage-skip-urs-warning">
                        Czy na pewno chcesz pominąć etap URS? Powiązanie procesu z
                        dokumentem URS zostanie usunięte, a etap otrzyma status „Pominięty”.
                    </Localized>
                </DialogContentText>

                <TextField
                    label="Powód pominięcia"
                    placeholder="Opcjonalnie podaj powód pominięcia etapu URS..."
                    fullWidth
                    multiline
                    minRows={3}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />

                {serverError && (
                    <Box mt={2} sx={{ color: (t) => t.palette.error.main, fontSize: 14 }}>
                        {serverError}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <LoadingButton
                    variant="contained"
                    color="error"
                    size="small"
                    loading={saving}
                    onClick={handleConfirm}
                >
                    <Localized id="stage-skip-urs-confirm">
                        Pomiń etap URS
                    </Localized>
                </LoadingButton>
                <CancelButton loading={saving} onClick={onClose} />
            </DialogActions>
        </Dialog >
    );
};
